import { Form } from '@betterfunnel/types';
import {
  CircleStackIcon,
  PencilIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { ListBulletIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useMemo, useState } from 'react';
import Button from '../components/Common/Button';
import IconButton from '../components/Common/IconButton';
import MenuButton from '../components/Common/MenuButton';
import { CustomDataGrid } from '../components/CustomDataGrid/CustomDataGrid';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import CreateFormModal from '../modals/CreateFormModal';
import EditStepperFormModal from '../modals/EditStepperFormModal/EditStepperFormModal';
import { deleteForm, duplicateForm } from '../services/FormsService';
import { track } from '../utils/analytics';

export default function Forms() {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { forms } = useContext(DashboardContext);
  const [createSimpleFormDialogOpen, setCreateSimpleFormDialogOpen] =
    useState(false);
  const [createStepperFormDialogOpen, setCreateStepperFormDialogOpen] =
    useState(false);
  const [editingForm, setEditingForm] = useState<{
    form: Form;
    id: string;
  } | null>(null);

  const onDeleteFormClick = useCallback(
    (formId: string) => {
      if (!selectedAccount) return;

      deleteForm(selectedAccount.id, formId)
        .then(() => {
          show('Deleted!');
        })
        .catch((e) => {
          show('Failed deleting form, ' + e.message || e.toString());
          Sentry.captureException(e);
        });
    },
    [selectedAccount, show],
  );

  const onDuplicateFormClick = useCallback(
    (form: Form) => {
      if (!selectedAccount) return;

      duplicateForm(selectedAccount.id, form, firebaseUser?.uid ?? '')
        .then(() => {
          show('Duplicated form');
        })
        .catch((e) => {
          show('Failed duplicating form, ' + e.message || e.toString());
          Sentry.captureException(e);
        });
    },
    [selectedAccount, firebaseUser?.uid, show],
  );

  const columns: ColumnDef<QueryDocumentSnapshot<Form, Form>>[] =
    useMemo(() => {
      return [
        {
          id: 'name',
          header: 'Name',
          accessorFn: (row) => row.data().name,
          size: 350,
          cell: ({ row: { original } }) => {
            return (
              <div className="flex flex-col">
                <div className="text-gray-900">{original.data().name}</div>
                <div className="text-gray-500">{original.id}</div>
              </div>
            );
          },
        },
        {
          id: 'formType',
          header: 'Type',
          accessorFn: (row) => {
            return row.data().formType ?? 'simple';
          },
          cell: ({ row: { original } }) => {
            const form = original.data();
            if (form.formType === 'stepper') {
              return 'Stepper';
            }
            return 'Simple';
          },
          size: 150,
        },
        {
          id: 'fields',
          header: 'Number of fields',
          accessorFn: (row) => {
            const form = row.data();
            if (form.formType === 'stepper') {
              return form.steps.reduce((acc, step) => {
                return acc + ('fields' in step ? step.fields.length : 0);
              }, 0);
            }
            return form.fields.length;
          },
          size: 250,
        },
        {
          id: 'createdAt',
          header: 'Created At',
          accessorFn: (row) => row.data().createdAt.toDate(),
          size: 200,
          cell: (info) => {
            return info.row.original.data().createdAt.toDate().toLocaleString();
          },
        },
        {
          header: '',
          accessorKey: 'actions',
          size: 100,
          cell: ({ row: { original } }) => (
            <div className="flex items-center gap-2">
              <IconButton
                variant="ternary"
                onClick={() => {
                  track('edit_form_button_click', {
                    formId: original.id,
                  });
                  setEditingForm({
                    form: original.data(),
                    id: original.id,
                  });
                  if (original.data().formType === 'stepper') {
                    setCreateStepperFormDialogOpen(true);
                  } else {
                    setCreateSimpleFormDialogOpen(true);
                  }
                }}
              >
                <PencilIcon className="h-4 w-4" />
              </IconButton>
              <MenuButton
                items={[
                  {
                    title: 'Duplicate',
                    onClick: () => onDuplicateFormClick(original.data()),
                  },
                  {
                    title: 'Delete',
                    destructive: true,
                    onClick: () => onDeleteFormClick(original.id),
                  },
                ]}
              ></MenuButton>
            </div>
          ),
        },
      ] satisfies ColumnDef<QueryDocumentSnapshot<Form, Form>>[];
    }, [onDeleteFormClick, onDuplicateFormClick]);

  return (
    <>
      <CreateFormModal
        form={
          editingForm?.form.formType !== 'stepper'
            ? editingForm?.form
            : undefined
        }
        formId={editingForm?.id}
        open={createSimpleFormDialogOpen}
        onClose={() => {
          setCreateSimpleFormDialogOpen(false);
          setEditingForm(null);
        }}
      />
      <EditStepperFormModal
        form={
          editingForm?.form.formType === 'stepper'
            ? editingForm?.form
            : undefined
        }
        formId={editingForm?.id}
        open={createStepperFormDialogOpen}
        onClose={() => {
          setCreateStepperFormDialogOpen(false);
          setEditingForm(null);
        }}
      />
      <div className="flex h-full flex-col py-8">
        <div className="pb-8 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Forms
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Here you can manage your forms and create new ones.
            </p>
          </div>

          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <MenuButton
              items={[
                {
                  icon: ListBulletIcon,
                  title: 'New Simple Form',
                  onClick: () => {
                    track('create_simple_form_button_click');
                    setCreateSimpleFormDialogOpen(true);
                  },
                },
                {
                  icon: CircleStackIcon,
                  title: 'New Stepper',
                  onClick: () => {
                    track('create_stepper_form_button_click');
                    setCreateStepperFormDialogOpen(true);
                  },
                },
              ]}
              customButton={
                <Button icon={<PlusIcon className="h-4 w-4 text-white" />}>
                  New
                </Button>
              }
            />
          </div>
        </div>

        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="Forms"
            columns={columns}
            data={forms}
            rowClassName="min-h-20"
            initialState={{
              columnPinning: {
                right: ['actions'],
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
